import { css } from '@emotion/react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { useCallback } from 'react';

import Cell from 'shopper/components/Grid/Cell';

import useClientSide from 'hooks/useClientSide';

import { useTopbar } from 'providers/TopbarProvider';

const SPACE_HEIGHT_BETWEEN_SIDEBAR_COMPONENTS_IN_PX = 16;
const RIGHT = 'right';
const LEFT = 'left';

const StyledSidebar = styled(Cell)`
  ${({ theme, isFixedAfterIgnoredRefs, offsetTop }) => css`
    height: auto;
    transition: ${theme.transitions.slower};
    will-change: top;

    ${theme.mediaQueries.xl} {
      display: block;
      height: fit-content;
      position: ${isFixedAfterIgnoredRefs ? 'sticky' : 'relative'};
      top: ${isFixedAfterIgnoredRefs ? `${offsetTop}px` : 0};
    }
  `}
`;

const sumRefsHeightAddingSpacings = (refsToBeIgnoredBeforeFixing) => {
  if (refsToBeIgnoredBeforeFixing.length === 0) {
    return 0;
  }

  const refsWithElement = refsToBeIgnoredBeforeFixing.filter(
    ({ current }) => current
  );

  return refsWithElement.reduce(
    (acc, { current: elem }, index) =>
      (index > 0 ? SPACE_HEIGHT_BETWEEN_SIDEBAR_COMPONENTS_IN_PX : 0) +
      acc +
      elem?.offsetHeight,
    0
  );
};

const Sidebar = ({
  children,
  isFixedAfterIgnoredRefs,
  position,
  refsToBeIgnoredBeforeFixing,
  ...rest
}) => {
  const isClientSide = useClientSide();
  const {
    topbarDesktop: {
      isTopbarActive,
      isTopbarBottomActive,
      constants: { topbarBottomHeight, topbarMainHeight },
    },
  } = useTopbar();

  const gridColumnStart =
    (position === RIGHT && [null, null, null, null, 13]) ||
    (position === LEFT && [null, null, null, null, 1]) ||
    null;

  const getCurrentTopbarOffset = useCallback(() => {
    if (!isClientSide) {
      return null;
    }

    return isTopbarBottomActive
      ? topbarMainHeight +
          topbarBottomHeight -
          sumRefsHeightAddingSpacings(refsToBeIgnoredBeforeFixing)
      : topbarMainHeight -
          sumRefsHeightAddingSpacings(refsToBeIgnoredBeforeFixing);
  }, [isClientSide, isTopbarActive, isTopbarBottomActive]);

  return (
    <StyledSidebar
      as="aside"
      gridColumnStart={gridColumnStart}
      isFixedAfterIgnoredRefs={isFixedAfterIgnoredRefs}
      offsetTop={getCurrentTopbarOffset()}
      gridColumnEnd={['span 4', null, 'span 12', null, 'span 4']}
      gridRowStart={[4, null, null, null, 1]}
      gridRowEnd={[5, null, null, null, 3]}
      mt={[4, null, null, null, 0]}
      {...rest}
    >
      {children}
    </StyledSidebar>
  );
};

Sidebar.defaultProps = {
  isFixedAfterIgnoredRefs: true,
  refsToBeIgnoredBeforeFixing: [],
  position: RIGHT,
};

Sidebar.propTypes = {
  children: PropTypes.node.isRequired,
  isFixedAfterIgnoredRefs: PropTypes.bool,
  refsToBeIgnoredBeforeFixing: PropTypes.arrayOf(PropTypes.shape()),
  position: PropTypes.oneOf([LEFT, RIGHT]),
};

export default Sidebar;
